import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Info, Plus, RefreshCw, X } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../../../services/api";
import { usePermissions } from "../../../rbac/PermissionsContext";
import { ADMIN_RBAC_CATALOG, getScreenCatalogForRole, normalizeAccess } from "../../../rbac/catalog";
import { DsAlert, DsButton, DsIcon, DsPageHeader } from "../../../design-system";
import RoleListView, { visibleRolesFor } from "./RoleListView";
import RolePermissionMatrix from "./RolePermissionMatrix";
import RoleSummaryPanel from "./RoleSummaryPanel";
import RoleUsersView from "./RoleUsersView";
import RoleWizard from "./RoleWizard";
import ConfirmDialog from "./ConfirmDialog";
import {
  apiErrorMessage,
  isModuleGranted,
  isSystemRole,
  moduleStats,
  normalizeData,
  roleUsesAccess,
} from "./roleAccessUtils";

/**
 * Roles & permissions page: Roles tab (list + matrix) | Users tab (assignments).
 * All role edits go through RoleWizard. Props come from GlobalSettingsModal.
 */
export default function UserRoleManagement({
  roles,
  fetchRoles,
  rolesMeta,
  setFormData,
  filterResults,
  setFilterResults,
  setShowFilters,
  branches,
  departments,
}) {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();

  const canViewUserRoles =
    isAdmin ||
    hasModuleAccess("user_roles", "view") ||
    hasScreenAccess("user_roles", "role_list", "view") ||
    hasScreenAccess("user_roles", "screen_access", "view") ||
    hasScreenAccess("user_roles", "data_access", "view");
  const canCreateRole = isAdmin || hasModuleAccess("user_roles", "create") || hasScreenAccess("user_roles", "role_list", "create");
  const canEditRole = isAdmin || hasModuleAccess("user_roles", "edit") || hasScreenAccess("user_roles", "role_list", "edit");
  const canDeleteRole = isAdmin || hasModuleAccess("user_roles", "delete") || hasScreenAccess("user_roles", "role_list", "delete");
  const canEditScreenAccess = isAdmin || hasModuleAccess("user_roles", "edit") || hasScreenAccess("user_roles", "screen_access", "edit");
  const canEditDataAccess = isAdmin || hasModuleAccess("user_roles", "edit") || hasScreenAccess("user_roles", "data_access", "edit");
  // PUT /users/:id/roles is admin-only on the API.
  const canRemoveCustom = isAdmin;

  const [mainTab, setMainTab] = useState("roles"); // roles | users
  const [search, setSearch] = useState("");
  const [showDefaultRoles, setShowDefaultRoles] = useState(false);
  const [selectedRoleId, setSelectedRoleId] = useState(null);
  const [rawScreenAccess, setRawScreenAccess] = useState({});
  const [dataByRole, setDataByRole] = useState({});
  const [accessLoading, setAccessLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);
  const [roleUsers, setRoleUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState(null);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [wizard, setWizard] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [infoRoleId, setInfoRoleId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyUserId, setBusyUserId] = useState(null);

  const loadAccess = useCallback(async () => {
    setAccessLoading(true);
    setAccessError(null);
    try {
      const [screenRes, dataRes] = await Promise.all([
        api.get("/global-settings/screen-access"),
        api.get("/global-settings/data-access"),
      ]);
      setRawScreenAccess(screenRes?.data?.data || {});
      setDataByRole(dataRes?.data?.data || {});
    } catch (err) {
      setAccessError(apiErrorMessage(err, "Could not load role permissions."));
    } finally {
      setAccessLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewUserRoles) loadAccess();
  }, [canViewUserRoles, loadAccess]);

  const loadRoleUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError(null);
    try {
      const res = await api.get("/global-settings/role-users");
      setRoleUsers(Array.isArray(res?.data?.data) ? res.data.data : []);
      setUsersLoaded(true);
    } catch (err) {
      setUsersError(apiErrorMessage(err, "Could not load users."));
    } finally {
      setUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    if (canViewUserRoles && mainTab === "users" && !usersLoaded && !usersLoading) {
      loadRoleUsers();
    }
  }, [canViewUserRoles, mainTab, usersLoaded, usersLoading, loadRoleUsers]);

  const roleList = useMemo(() => (Array.isArray(roles) ? roles : []), [roles]);

  const accessByRole = useMemo(() => {
    const out = {};
    for (const r of roleList) {
      out[r.id] = normalizeAccess(rawScreenAccess[String(r.id)] || {}, getScreenCatalogForRole(r));
    }
    return out;
  }, [roleList, rawScreenAccess]);

  // Non-admins reaching this page are delegates (custom role with User Roles); the backend
  // enforces the same limits — this only shapes the UI.
  const delegate = useMemo(() => {
    if (isAdmin) return null;
    const hinted = roleList.find((r) => Array.isArray(r.editableModules));
    const own = roleList.find((r) => r.isOwnRole);
    const ownData = own ? normalizeData(dataByRole[String(own.id)]) : null;
    return {
      modules: new Set(hinted?.editableModules || []),
      ownScope: ownData?.level ? ownData : { level: "own", selectedItems: [] },
    };
  }, [isAdmin, roleList, dataByRole]);

  const visibleRoles = useMemo(
    () => visibleRolesFor(roleList, { search, showDefaultRoles }),
    [roleList, search, showDefaultRoles]
  );

  useEffect(() => {
    if (!visibleRoles.some((r) => String(r.id) === String(selectedRoleId))) {
      setSelectedRoleId(visibleRoles[0]?.id ?? null);
    }
  }, [visibleRoles, selectedRoleId]);

  const selectedRole = roleList.find((r) => String(r.id) === String(selectedRoleId)) || null;
  const selectedUsesAccess = roleUsesAccess(selectedRole);
  const selectedCatalog = getScreenCatalogForRole(selectedRole);
  const selectedAccess = selectedRole ? accessByRole[selectedRole.id] : null;

  const infoRole = roleList.find((r) => String(r.id) === String(infoRoleId)) || null;
  const infoCatalog = getScreenCatalogForRole(infoRole);
  const infoAccess = infoRole ? accessByRole[infoRole.id] : null;
  const infoStats = useMemo(() => {
    const rows = infoCatalog.map((m) => moduleStats(m, infoAccess?.[m.key]));
    return {
      modulesOn: rows.filter((r) => r.granted > 0).length,
      modulesTotal: infoCatalog.length,
      permissions: rows.reduce((sum, r) => sum + r.granted, 0),
    };
  }, [infoCatalog, infoAccess]);

  const getBlockers = useCallback(
    (role) => {
      if (!role) return {};
      const system = isSystemRole(role);
      const catalog = getScreenCatalogForRole(role);
      const access = accessByRole[role.id];
      const own = role.isOwnRole ? "You can't change your own role." : null;
      const outside =
        delegate &&
        catalog.some((m) => !delegate.modules.has(m.key) && isModuleGranted(m, access?.[m.key]));
      const outsideMsg = (what) =>
        `This role includes modules you can't grant, so only an admin can ${what} it.`;
      const canEditThis = system ? canEditScreenAccess || canEditDataAccess : canEditRole;
      return {
        edit: !canEditThis ? "You don't have permission to edit roles." : own,
        copy: !canCreateRole ? "You don't have permission to create roles." : null,
        toggle: !canEditRole
          ? "You don't have permission to edit roles."
          : own || (outside && role.active ? outsideMsg("deactivate") : null),
        delete: !canDeleteRole
          ? "You don't have permission to delete roles."
          : own || (outside ? outsideMsg("delete") : null),
      };
    },
    [
      accessByRole,
      delegate,
      canEditRole,
      canCreateRole,
      canDeleteRole,
      canEditScreenAccess,
      canEditDataAccess,
    ]
  );

  const afterChange = async () => {
    await Promise.all([
      fetchRoles(),
      loadAccess(),
      mainTab === "users" || usersLoaded ? loadRoleUsers() : Promise.resolve(),
    ]);
  };

  const removeCustomAccess = async (user) => {
    if (!user?.userId) return;
    setBusyUserId(user.userId);
    try {
      await api.put(`/global-settings/users/${user.userId}/roles`, { roleCodes: ["EMPLOYEE"] });
      toast.success(`${user.name} reset to Employee / Self.`);
      setConfirm(null);
      await Promise.all([fetchRoles(), loadRoleUsers()]);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not remove custom role access."));
    } finally {
      setBusyUserId(null);
    }
  };

  const setActive = async (role, active) => {
    setBusy(true);
    try {
      await api.put(`/global-settings/roles/${role.id}`, { active });
      toast.success(active ? `${role.name} reactivated.` : `${role.name} deactivated.`);
      setConfirm(null);
      await afterChange();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not update the role status."));
    } finally {
      setBusy(false);
    }
  };

  const deleteRole = async (role) => {
    setBusy(true);
    try {
      await api.delete(`/global-settings/roles/${role.id}`);
      toast.success(`${role.name} deleted.`);
      setConfirm(null);
      setSelectedRoleId(null);
      setInfoRoleId((id) => (String(id) === String(role.id) ? null : id));
      await afterChange();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Could not delete the role."));
    } finally {
      setBusy(false);
    }
  };

  const handleRoleAction = useCallback(
    (kind, role) => {
      if (!role) return;
      setSelectedRoleId(role.id);
      if (kind === "info") {
        setInfoRoleId(role.id);
        return;
      }
      if (kind === "edit") {
        setWizard({ role });
        return;
      }
      if (kind === "copy") {
        setWizard({ copyFromRoleId: role.id });
        return;
      }
      if (kind === "toggle") {
        if (role.active) setConfirm({ kind: "deactivate", role });
        else setActive(role, true);
        return;
      }
      if (kind === "delete") setConfirm({ kind: "delete", role });
    },
    // setActive closes over latest fetch/load; recreate when those identities change is unnecessary
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  if (!canViewUserRoles) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <DsAlert tone="danger">You do not have access to User Role Management.</DsAlert>
      </div>
    );
  }

  const delegateModuleNames = delegate
    ? ADMIN_RBAC_CATALOG.filter((m) => delegate.modules.has(m.key)).map((m) => m.label)
    : [];

  const pageInfoText =
    "Roles: create and manage custom roles, view permissions, and use the ⋮ menu for Info, Edit, Copy access, Deactivate, or Delete. Users: see every active employee’s assigned role — Default shows Employee / Self or Reporting Manager; Custom shows the custom role name. Remove custom access resets that employee to Employee / Self in one step.";

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto bg-[hsl(var(--ds-bg))] p-4 lg:overflow-hidden">
      <DsPageHeader
        className="!mb-0 shrink-0"
        title="Roles & permissions"
        description={
          mainTab === "users"
            ? "See each employee’s assigned role. Default roles show Employee / Self or Reporting Manager; custom roles show by name. Remove custom access to reset to Employee / Self."
            : "Choose a role to see what it can access. Use Create role or the ⋮ menu to change access."
        }
        actions={
          mainTab === "roles" ? (
            <DsButton onClick={() => setWizard({})} disabled={!canCreateRole} title={!canCreateRole ? "You don't have permission to create roles." : undefined}>
              <DsIcon icon={Plus} size="sm" />
              Create role
            </DsButton>
          ) : null
        }
      />

      <div className="flex shrink-0 flex-wrap items-center gap-2">
        <div className="inline-flex rounded-full border border-[hsl(var(--ds-border))] p-0.5">
          {[
            { id: "roles", label: "Roles" },
            { id: "users", label: "Users" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setMainTab(opt.id)}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                mainTab === opt.id
                  ? "border border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]"
                  : "border border-transparent text-[hsl(var(--ds-primary))]/80 hover:text-[hsl(var(--ds-primary))]"
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <span
          title={pageInfoText}
          className="inline-flex h-5 w-5 shrink-0 cursor-help items-center justify-center rounded-full border border-[hsl(var(--ds-primary)/0.35)] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)]"
          aria-label={pageInfoText}
        >
          <Info size={12} strokeWidth={2} className="h-3 w-3" />
        </span>
      </div>

      {mainTab === "roles" && accessError ? (
        <DsAlert tone="danger" className="flex shrink-0 flex-wrap items-center justify-between gap-2">
          <span>{accessError}</span>
          <DsButton size="sm" variant="outline" onClick={loadAccess}>
            <DsIcon icon={RefreshCw} size="sm" />
            Retry
          </DsButton>
        </DsAlert>
      ) : null}

      {mainTab === "roles" && delegate ? (
        <DsAlert tone="info" className="shrink-0">
          You can manage roles for{" "}
          <strong>{delegateModuleNames.length ? delegateModuleNames.join(", ") : "no modules yet"}</strong>.
          Other modules on a role stay locked and unchanged. You can&apos;t edit your own role.
        </DsAlert>
      ) : null}

      {mainTab === "roles" ? (
        <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[300px_minmax(0,1fr)]">
          <div className="flex max-h-[420px] min-h-0 flex-col lg:max-h-none">
            <RoleListView
              roles={roleList}
              visibleRoles={visibleRoles}
              search={search}
              setSearch={setSearch}
              showDefaultRoles={showDefaultRoles}
              setShowDefaultRoles={setShowDefaultRoles}
              selectedRoleId={selectedRoleId}
              onSelectRole={setSelectedRoleId}
              isDelegate={!!delegate}
              getBlockers={getBlockers}
              onRoleAction={handleRoleAction}
            />
          </div>
          <div className="flex min-h-[360px] flex-col lg:min-h-0">
            <RolePermissionMatrix
              key={selectedRole?.id ?? "none"}
              role={selectedRole}
              catalog={selectedCatalog}
              access={selectedAccess}
              usesAccess={selectedUsesAccess}
              loading={accessLoading}
            />
          </div>
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col">
          <RoleUsersView
            users={roleUsers}
            loading={usersLoading}
            error={usersError}
            onRetry={loadRoleUsers}
            canRemoveCustom={canRemoveCustom}
            busyUserId={busyUserId}
            onRemoveCustom={(user) => setConfirm({ kind: "removeCustom", user })}
          />
        </div>
      )}

      {infoRole
        ? createPortal(
            <div
              className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/50 p-4"
              role="presentation"
              onMouseDown={(e) => {
                if (e.target === e.currentTarget) setInfoRoleId(null);
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-labelledby="urm-role-info-title"
                className="flex max-h-[min(85vh,640px)] w-full max-w-md flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl"
              >
                <div className="flex shrink-0 items-center justify-between gap-2 border-b border-[hsl(var(--ds-border))] px-4 py-3">
                  <h3 id="urm-role-info-title" className="ds-h4 text-[hsl(var(--ds-fg))]">
                    Role info
                  </h3>
                  <button
                    type="button"
                    aria-label="Close"
                    onClick={() => setInfoRoleId(null)}
                    className="flex h-8 w-8 items-center justify-center rounded-md text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))]"
                  >
                    <DsIcon icon={X} size="sm" />
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto p-3">
                  <RoleSummaryPanel
                    role={infoRole}
                    isSystem={isSystemRole(infoRole)}
                    usesAccess={roleUsesAccess(infoRole)}
                    stats={infoStats}
                    dataAccess={normalizeData(dataByRole[String(infoRole.id)])}
                    branches={branches}
                    departments={departments}
                    showActions={false}
                    className="flex min-h-0 flex-col overflow-hidden bg-transparent"
                  />
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

      {wizard ? (
        <RoleWizard
          target={wizard}
          roles={roleList}
          accessByRole={accessByRole}
          dataByRole={dataByRole}
          branches={branches}
          departments={departments}
          filterResults={filterResults}
          setFilterResults={setFilterResults}
          setShowFilters={setShowFilters}
          setFormData={setFormData}
          activeEmployeeCount={rolesMeta?.activeEmployeeCount}
          isAdmin={isAdmin}
          delegate={delegate}
          canEditScreenAccess={canEditScreenAccess}
          canEditDataAccess={canEditDataAccess}
          onClose={() => setWizard(null)}
          onRoleCreated={() => fetchRoles()}
          onSaved={async (roleId) => {
            setWizard(null);
            setShowDefaultRoles((prev) => prev || isSystemRole(roleList.find((r) => String(r.id) === String(roleId))));
            await afterChange();
            setSelectedRoleId(roleId);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={confirm?.kind === "deactivate"}
        tone="warning"
        title={`Deactivate ${confirm?.role?.name || "role"}?`}
        confirmLabel="Deactivate"
        loading={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => setActive(confirm.role, false)}
      >
        {confirm?.role?.assignedUserCount
          ? `${confirm.role.assignedUserCount} assigned employee(s) will get only default employee access until you reactivate it.`
          : "No employees are assigned to this role right now."}{" "}
        The role&apos;s settings are kept.
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "delete"}
        title={`Delete ${confirm?.role?.name || "role"}?`}
        confirmLabel="Delete role"
        loading={busy}
        onCancel={() => setConfirm(null)}
        onConfirm={() => deleteRole(confirm.role)}
      >
        This permanently removes the role and its permissions.
        {confirm?.role?.assignedUserCount
          ? ` ${confirm.role.assignedUserCount} assigned employee(s) go back to default employee access.`
          : ""}{" "}
        This cannot be undone.
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "removeCustom"}
        tone="warning"
        title={`Remove custom access for ${confirm?.user?.name || "user"}?`}
        confirmLabel="Remove custom access"
        loading={!!busyUserId}
        onCancel={() => setConfirm(null)}
        onConfirm={() => removeCustomAccess(confirm.user)}
      >
        {confirm?.user?.roleName
          ? `This clears the “${confirm.user.roleName}” custom role and resets them to the default Employee / Self role.`
          : "This clears all custom role access and resets them to the default Employee / Self role."}{" "}
        They keep ESS / self-service access only until assigned another custom role.
      </ConfirmDialog>
    </div>
  );
}
